import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getFileIdByName, resetFileIdByName } from "../../state/actions/getFileIdByNameAction";
import { updateFileUrl, resetupdateFileUrl } from "../../state/actions/updateFileUrlAction";
import { getFileStatus, resetgetfilestatus } from "../../state/actions/getUploadFileStatusAction";
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../features/auth/AuthContext';
import { toast } from "react-toastify";
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/build/pdf';
import ExcelJS from 'exceljs';
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";

GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist/build/pdf.worker.min.js';

export function useAWSS3FileUpload({
    selectedFiles,
    setSelectedFiles,
    setuploadedFileLink,
    setShowUploadPop,
    selectedRepoFiles
}) {

    const [filesToUpload, setfilesToUpload] = useState([]);
    const [fileCollectionMap, setFileCollectionMap] = useState({});

    const { token } = useAuth();
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();

    const { fileList } = useSelector((state) => state.getFileIdByName);
    const { fileStatus } = useSelector((state) => state.getFileUploadStatus);

    const handleIconClick = () => {
        fileInputRef.current.click();
    };

    const checkPasswordProtection = (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();

            fileReader.onload = async (e) => {
                try {
                    const typedArray = new Uint8Array(e.target.result);
                    const loadingTask = getDocument({ data: typedArray });

                    await loadingTask.promise.then(
                        () => resolve({ isProtected: false, isCorrupted: false }),
                        (error) => {
                            if (error.name === 'PasswordException') {
                                resolve({ isProtected: true, isCorrupted: false });
                            } else if (error.name === 'InvalidPDFException') {
                                resolve({ isProtected: false, isCorrupted: true });
                            } else {
                                reject(error);
                            }
                        }
                    );
                } catch (error) {
                    reject(error);
                }
            };

            fileReader.readAsArrayBuffer(file);
        });
    };


    function checkExcelFile(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    // Security: migrated from `xlsx` (had unfixable HIGH CVEs) to `exceljs`.
                    // Same validation purpose: load the file and reject if parsing fails.
                    const workbook = new ExcelJS.Workbook();
                    await workbook.xlsx.load(e.target.result);
                    resolve({ isProtected: false, isCorrupted: false });
                } catch (error) {
                    const msg = (error && error.message) || '';
                    if (msg.includes('password-protected') || msg.includes('File is password-protected') || msg.includes('encrypted')) {
                        resolve({ isProtected: true, isCorrupted: false });
                    } else {
                        resolve({ isProtected: false, isCorrupted: true });
                    }
                }
            };

            reader.onerror = () => {
                resolve({ isProtected: false, isCorrupted: true });
            };

            reader.readAsArrayBuffer(file);
        });
    }


    const checkWordFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = function (e) {
                try {
                    const content = e.target.result;
                    const zip = new PizZip(content);
                    new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });
                    resolve({ isProtected: false, isCorrupted: false });
                } catch (error) {
                    console.error("Error processing the file:", error);
                    resolve({ isProtected: false, isCorrupted: true });
                }
            };

            reader.onerror = function (e) {
                reject(e);
            };

            reader.readAsArrayBuffer(file);
        });
    };


    function checkImageCorruption(file) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(false);
            img.onerror = () => resolve(true);

            const reader = new FileReader();
            reader.onload = e => img.src = e.target.result;
            reader.onerror = e => reject(e);
            reader.readAsDataURL(file);
        });
    }

    const readFileContent = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read the file.'));
            reader.readAsText(file);
        });
    };

    const checkCorruption = (content) => {
        return /[\x00-\x08\x0E-\x1F]/.test(content);
    };

    const checkForProtection = (content) => {
        return content.startsWith("PROTECTED:");
    };

    const handleFileChange = async (e) => {

        let incomingFiles = e.target.files;
        let updatedFiles = Array.from(selectedFiles);
        let incomingNewFiles = Array.from(e.target.files);

        const duplicateFile = incomingNewFiles.find(incomingFile =>
            updatedFiles.some(selectedFile => selectedFile.name === incomingFile.name)
        );

        if (duplicateFile) {
            toast.error(`File with name "${duplicateFile.name}" already exists.`);
            return; // Stop further processing if a duplicate is found
        }

        if(updatedFiles?.length + incomingNewFiles?.length + selectedRepoFiles?.length > 2){
            return toast.error("You can only upload 2 files with a total file size of 40MB.")
        }

        let newFiles = [];
        setShowUploadPop(false);
        let totalSize = updatedFiles.reduce((acc, file) => acc + file.size, 0);
        const allowedExtensions = ['pdf', 'docx', 'doc', 'xls', 'xlsx', 'csv', 'png', 'jpg', 'jpeg', 'txt'];

        for (let i = 0; i < incomingFiles.length; i++) {
            const file = incomingFiles[i];
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (!allowedExtensions.includes(fileExtension)) {
                toast.error(`File type not supported: ${file.name}`);
                continue;
            }

            if (file.size === 0) {
                toast.error(`${file.name} seems corrupted.`);
                continue;
            }

            if (fileExtension === 'pdf') {
                try {
                    const { isProtected, isCorrupted } = await checkPasswordProtection(file);

                    if (isProtected) {
                        toast.error(`${file.name} is password protected.`);
                        continue;
                    } else if (isCorrupted) {
                        toast.error(`${file.name} is corrupted or invalid.`);
                        continue;
                    }
                } catch (error) {
                    // toast.error(`Error checking ${file.name}: ${error.message}`);
                }
            }

            const spreadsheetExtensions = ["xlsx", "xls", "xlsm", "xlsb", "csv", "ods"];

            if (spreadsheetExtensions.includes(fileExtension)) {
                try {
                    const { isProtected, isCorrupted } = await checkExcelFile(file);

                    if (isProtected) {
                        toast.error(`${file.name} is password protected.`);
                        continue;
                    } else if (isCorrupted) {
                        toast.error(`${file.name} is corrupted or invalid.`);
                        continue;
                    }
                } catch (error) {
                    toast.error(`Error processing ${file.name}: ${error.message}`);
                    continue;
                }
            }

            const docExtensions = ["doc", "docx"];

            if (docExtensions.includes(fileExtension)) {
                try {
                    const { isProtected, isCorrupted } = await checkWordFile(file);

                    if (isProtected) {
                        toast.error(`${file.name} is password protected or corrupted.`);
                        continue;
                    }

                } catch (error) {
                    console.error("Error processing the file:", error);
                }
            }

            const imageExtensions = ["png", "jpg", "jpeg"];

            if (imageExtensions.includes(fileExtension)) {
                try {
                    const isCorrupted = await checkImageCorruption(file);

                    if (isCorrupted) {
                        toast.error(`${file.name} is corrupted or invalid.`);
                        continue;
                    }
                } catch (error) {
                    // toast.error(`Error checking ${file.name}: ${error.message}`);
                }
            }

            if (fileExtension === "txt") {
                try {
                    const fileContent = await readFileContent(file);

                    const isCorrupted = checkCorruption(fileContent);
                    if (isCorrupted) {
                        toast.error(`${file.name} appears to be corrupted.`);
                        continue;
                    }

                    const isProtected = checkForProtection(fileContent);
                    if (isProtected) {
                        toast.error(`${file.name} appears to be password protected.`);
                        continue;
                    }

                } catch (error) {
                    toast.error(`Error processing ${file.name}: ${error.message}`);
                }
            }


            if (updatedFiles.length < 2 && totalSize + file.size <= 40 * 1024 * 1024) {
                updatedFiles.push(file);
                newFiles.push(file)
                totalSize += file.size;
            } else {
                toast.error("You can only upload 2 files with a total file size of 40MB.");
                break;
            }
        }
        if (updatedFiles.length <= 2 && totalSize <= 40 * 1024 * 1024) {
            setSelectedFiles(updatedFiles);
            setfilesToUpload(newFiles);
        } else {
            e.target.value = null;
        }
    };

    const handleFileStatus = (file_id) => {
        dispatch(getFileStatus(file_id, token))
    }

    const handleUpload = async () => {
        if (!filesToUpload.length) {
            alert('Please choose a file to upload first.');
            return;
        }
    
        try {
            const newArr = [];
    
            for (const file of filesToUpload) {
                const matchingFile = fileList.find(f => f.original_name === file.name);
                if (matchingFile) {
                    const { presigned_url, file_id } = matchingFile;
                    await uploadFileToS3(file, presigned_url);
    
                    const fileLink = {
                        file_id: file_id,
                        name: file.name,
                        url: presigned_url.split('?')[0],
                        collection_name: null, // Will be populated when file status is retrieved
                    };
    
                    newArr.push(fileLink);
    
                    const req = {
                        file_upload: [{
                            file_id: file_id,
                            link: fileLink.url,
                        }]
                    };
    
                    dispatch(updateFileUrl(req, token));
                    handleFileStatus(file_id);
                    
                    setFileCollectionMap(prev => ({
                        ...prev,
                        [file_id]: file.name
                    }));
                } else {
                    console.error(`No matching file found for ${file.name}`);
                }
            }
    
            setuploadedFileLink(prev => [
                ...prev,
                ...newArr,
            ]);
    
            setfilesToUpload([]);
            dispatch(resetFileIdByName());
        } catch (error) {
            console.error('Error uploading files:', error);
        }
    };
    
    const uploadFileToS3 = async (file, url) => {
        try {
            await axios.put(url, file, {
                headers: {
                    'Content-Type': file.type,
                },
            });
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(`Error uploading ${file.name}`);
            throw error;
        }
    };

    useEffect(() => {
        if (fileList.length) {
            handleUpload();
        }
    }, [fileList]);

    useEffect(() => {
        if (filesToUpload.length) {
            dispatch(getFileIdByName({ file_name: filesToUpload.map(f => f.name) }, token));
        }
    }, [filesToUpload]);

    useEffect(() => {
        dispatch(resetgetfilestatus());
    }, [dispatch]);
    // Capture collection_name from the file-status poll and attach it to the
    // matching uploaded file. fileStatus holds only the latest completed file,
    // so patch on every change rather than reading it once.
    useEffect(() => {
        console.log("[poll] fileStatus:", fileStatus?.original_name, fileStatus?.collection_name);
        if (!fileStatus?.collection_name) return;

        const statusName = fileStatus.original_name;
        if (!statusName) return;

        setuploadedFileLink((prev) =>
            prev.map((file) =>
                file.name === statusName
                    ? { ...file, collection_name: fileStatus.collection_name }
                    : file
            )
        );
    }, [fileStatus, setuploadedFileLink]);
    useEffect(() => {
        return () => {
            setSelectedFiles([]);
            setFileCollectionMap({});
            dispatch(resetFileIdByName());
            dispatch(resetupdateFileUrl());
            dispatch(resetgetfilestatus());
        }
    }, [dispatch, setSelectedFiles]);

    return {
        handleIconClick,
        handleFileChange,
        fileInputRef,
        fileCollectionMap
    };
}
